<ng-template #createButton>
  <button color="primary" mat-flat-button test-id="create-button" (click)="editProtocol()">
    Create New Viewer Protocol
  </button>
</ng-template>

<mat-card appearance="outlined" [class.full-height]="unfilteredProtocolsExist">
  @if (unfilteredProtocolsExist || isLoading) {
    <mat-card-title>
      <div>
        <label>Viewer Protocols</label>
        <p class="text-muted">
          Protocols allow you to create custom workflows for capturing pertinent data from medical imaging inside the
          viewer.
        </p>
      </div>
      <div>
        <ng-container *ngTemplateOutlet="createButton" />
      </div>
    </mat-card-title>
  }

  @if (isLoading && !unfilteredProtocolsExist) {
    <mat-card-content>
      <mat-progress-bar mode="buffer" />
    </mat-card-content>
  }

  @if (!isLoading && !unfilteredProtocolsExist) {
    <mat-card-content class="empty">
      <ng-container *ngTemplateOutlet="empty" />
    </mat-card-content>
  }

  <mat-card-content [class.hidden]="!unfilteredProtocolsExist">
    <div class="table-wrapper">
      <table
        mat-table
        matSort
        matSortDisableClear
        [dataSource]="protocols$"
        [matSortActive]="protocolSort$.value.active"
        [matSortDirection]="protocolSort$.value.direction"
        (matSortChange)="protocolSort$.next($event)"
      >
        <ng-container matColumnDef="label">
          <th *matHeaderCellDef mat-header-cell>
            <mat-form-field appearance="outline">
              <input
                autocomplete="off"
                matInput
                placeholder="Protocol"
                test-id="protocol-label-filter"
                type="text"
                [formControl]="$any(filters.get('label'))"
                (click)="$event.stopPropagation()"
                (keydown)="$event.stopPropagation()"
              />
              @if (filters.get('label').value) {
                <button
                  fwTooltip="Click to clear input"
                  fwTooltipPosition="right"
                  mat-icon-button
                  matSuffix
                  test-id="label-filter-clear"
                  (click)="filters.get('label').reset()"
                >
                  <mat-icon>close</mat-icon>
                </button>
              }
            </mat-form-field>
          </th>
          <td *matCellDef="let element" mat-cell>
            {{ element.label }}
          </td>
        </ng-container>

        <ng-container matColumnDef="description">
          <th *matHeaderCellDef mat-header-cell>Description</th>
          <td *matCellDef="let element" mat-cell>
            <fw-ellipsis [text]="element?.description || ''" />
          </td>
        </ng-container>

        <ng-container matColumnDef="creator">
          <th *matHeaderCellDef mat-header-cell>
            <app-typeahead
              placeholder="Creator"
              test-id="creator-filter"
              [options]="userOptions"
              (click)="$event.stopPropagation()"
              (keydown)="$event.stopPropagation()"
              [(value)]="creatorFilter"
            />
          </th>
          <td *matCellDef="let element" mat-cell [fwTooltip]="element.creator?._id">
            <div class="flex-wrapper">
              <app-avatar [enableAnimation]="true" [enablePopover]="false" [users]="element.creator" />
              <span class="ellipsis-block"> {{ element.creator?.firstname }} {{ element.creator?.lastname }} </span>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="modified">
          <th *matHeaderCellDef mat-header-cell mat-sort-header>Last Modified</th>
          <td *matCellDef="let element" mat-cell>
            {{ element.lastModifiedDisplay }}
          </td>
        </ng-container>

        <ng-container matColumnDef="actions">
          <th *matHeaderCellDef mat-header-cell></th>
          <td *matCellDef="let element; let i = index" class="text-right list-action-buttons" mat-cell>
            <div class="list-action-buttons">
              <button mat-icon-button [attr.test-id]="'actions-trigger-' + i" [matMenuTriggerFor]="actionMenu">
                <mat-icon class="meatballs xs" iconPositionEnd [ngClass]="{ disabled: !canModify }">more_vert</mat-icon>
              </button>
              <mat-menu #actionMenu="matMenu" xPosition="before">
                <button
                  mat-menu-item
                  [attr.test-id]="'edit-action-' + i"
                  (click)="$event.stopPropagation(); editProtocol(element)"
                >
                  <fw-icon class="action-icon sm">edit</fw-icon>
                  Edit
                </button>
                <button
                  mat-menu-item
                  [attr.test-id]="'duplicate-action-' + i"
                  (click)="$event.stopPropagation(); duplicateProtocol(element)"
                >
                  <fw-icon class="action-icon sm">apply-copy-duplicate</fw-icon>
                  Duplicate
                </button>
                <button
                  mat-menu-item
                  [attr.test-id]="'delete-action-' + i"
                  (click)="$event.stopPropagation(); deleteProtocol(element)"
                >
                  <fw-icon class="action-icon sm red">trash-delete</fw-icon>
                  Delete
                </button>
              </mat-menu>
            </div>
          </td>
        </ng-container>

        <tr *matHeaderRowDef="columns; sticky: true" mat-header-row></tr>
        <tr *matRowDef="let row; columns: columns" class="list-action-hover" mat-row></tr>
      </table>
      <div #fwLoadTrigger class="load-trigger" (fwLoadTrigger)="loadMore()"></div>

      @if (isLoading && unfilteredProtocolsExist) {
        <fw-loading />
      }

      @if (!isLoading && !hasProtocols && !hasLoadingError && hasFilters) {
        <div>
          <ng-container *ngTemplateOutlet="empty" />
        </div>
      }

      @if (hasLoadingError) {
        <div class="empty blank-state">
          <mat-icon class="sm raised red">priority_high</mat-icon>
          <p>There was an error loading protocols.</p>
        </div>
      }
    </div>
  </mat-card-content>
</mat-card>

<ng-template #empty>
  <div class="empty empty-inside">
    <fw-icon>settings-gear-square</fw-icon>
    <p>No Viewer Protocols</p>
    <small class="body text-muted"
      >There are currently no viewer protocols. Viewer protocols allow you to create custom workflows for capturing
      pertinent data from medical imaging inside the viewer.</small
    >
    <small>
      <span class="text-muted">Want to learn more? </span>
      <a href="https://docs.flywheel.io/?q=creating+viewer+protocol" target="_blank" test-id="view-documentation-link"
        >View documentation</a
      >
    </small>
    @if (!unfilteredProtocolsExist) {
      <ng-container *ngTemplateOutlet="createButton" />
    }
  </div>
</ng-template>
